import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ROOT_URL } from '../../api/ApiConfig';

export const NOTIFICATION_SOCKET_EVENT = 'new_notification';

export const NOTIFICATION_SOCKET_EVENTS = [
  'new_notification',
  'notification:new',
];

function getAuthContext(state) {
  if (!state) return { authToken: null, business_id: null };
  const login = state.loginToken?.response;
  const co = state.loginTokenCoUser?.response;
  const token = login?.token ?? co?.token ?? co?.data?.token;
  const user = login?.user ?? co?.user ?? co?.data?.user;
  const business_id = user?.business_id;
  return { authToken: token, business_id };
}

function notificationBaseUrl(business_id) {
  return `${ROOT_URL}/notification/${business_id}`;
}

/**
 * GET /notification/:business_id?page&limit&isRead
 * @param {{ page?: number, limit?: number, isRead?: boolean, append?: boolean }} params
 */
export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (
    { page = 1, limit = 20, isRead, append = false } = {},
    { getState, rejectWithValue }
  ) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      const params = { page, limit };
      if (typeof isRead === 'boolean') params.isRead = isRead;

      const response = await axios.get(notificationBaseUrl(business_id), {
        headers: { Authorization: `Bearer ${authToken}` },
        params,
      });
      return { ...response.data, append };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchNotificationUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      const response = await axios.get(
        `${notificationBaseUrl(business_id)}/unread-count`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

function notificationMatchesTicket(n, ticketId) {
  if (!ticketId || !n?._id || n.isRead) return false;
  const tid = String(ticketId);
  if (n.referenceModel === 'Ticket' && String(n.referenceId) === tid) {
    return true;
  }
  if (
    n.referenceModel === 'TicketMessage' &&
    String(n.metadata?.ticketId || '') === tid
  ) {
    return true;
  }
  return false;
}

function notificationMatchesRoom(n, roomId) {
  if (!roomId || !n?._id || n.isRead) return false;
  if (n.referenceModel !== 'Room' && n.referenceModel !== 'Message') {
    return false;
  }
  const rid = String(roomId);
  const refRoom = String(n.metadata?.roomId ?? n.referenceId ?? '');
  return refRoom === rid;
}

function notificationMatchesArticle(n, articleId) {
  if (!articleId || !n?._id || n.isRead) return false;
  const aid = String(articleId);
  if (n.referenceModel === 'KBArticle') {
    if (String(n.referenceId) === aid) return true;
    if (String(n.metadata?.articleId || '') === aid) return true;
  }
  if (String(n.metadata?.articleId || '') === aid) return true;
  return false;
}


export const markNotificationsReadForContext = createAsyncThunk(
  'notifications/markReadForContext',
  async (
    { ticketId, roomId, articleId },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      if (!ticketId && !roomId && !articleId) {
        return { notificationIds: [] };
      }

      const matches = (n) => {
        if (ticketId && notificationMatchesTicket(n, ticketId)) return true;
        if (roomId && notificationMatchesRoom(n, roomId)) return true;
        if (articleId && notificationMatchesArticle(n, articleId)) return true;
        return false;
      };

      const collected = new Set();
      let page = 1;
      const limit = 50;
      const maxPages = 10;

      while (page <= maxPages) {
        const response = await axios.get(notificationBaseUrl(business_id), {
          headers: { Authorization: `Bearer ${authToken}` },
          params: { page, limit, isRead: false },
        });
        const raw = response.data?.data ?? response.data;
        const items = raw?.items ?? [];
        for (const n of items) {
          if (matches(n)) collected.add(n._id);
        }
        if (items.length < limit) break;
        page += 1;
      }

      const ids = [...collected];
      await Promise.all(
        ids.map((notificationId) =>
          axios.patch(
            `${notificationBaseUrl(business_id)}/${notificationId}/read`,
            {},
            { headers: { Authorization: `Bearer ${authToken}` } }
          )
        )
      );

      dispatch(fetchNotificationUnreadCount());
      return { notificationIds: ids };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'notifications/markRead',
  async (notificationId, { getState, rejectWithValue }) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      await axios.patch(
        `${notificationBaseUrl(business_id)}/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      return { notificationId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      const response = await axios.patch(
        `${notificationBaseUrl(business_id)}/read-all`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/delete',
  async (notificationId, { getState, rejectWithValue }) => {
    try {
      const state = typeof getState === 'function' ? getState() : null;
      const { authToken, business_id } = getAuthContext(state);
      if (!business_id) {
        return rejectWithValue('Missing auth or business_id');
      }
      await axios.delete(
        `${notificationBaseUrl(business_id)}/${notificationId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      return { notificationId };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  unreadCount: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  listLoading: false,
  listLoadingMore: false,
  unreadLoading: false,
  actionError: null,
  listError: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    resetNotificationsState: () => ({ ...initialState }),

  
    notificationReceivedFromSocket: (state, action) => {
      const n = action.payload;
      if (!n || !n._id) return;
      const exists = state.items.some((i) => i._id === n._id);
      if (!exists) {
        state.items = [n, ...state.items];
        state.total += 1;
      }
      if (n.isRead === false) {
        state.unreadCount = (state.unreadCount || 0) + (exists ? 0 : 1);
      }
    },

    notificationReadFromSocket: (state, action) => {
      const id = action.payload?.notificationId || action.payload?._id;
      if (!id) return;
      const item = state.items.find((i) => i._id === id);
      if (item && !item.isRead) {
        item.isRead = true;
        state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
      }
    },

    syncUnreadCount: (state, action) => {
      if (typeof action.payload === 'number') {
        state.unreadCount = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state, action) => {
        state.listError = null;
        if (action.meta.arg?.append) {
          state.listLoadingMore = true;
        } else {
          state.listLoading = true;
        }
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.listLoading = false;
        state.listLoadingMore = false;
        const raw = action.payload?.data ?? action.payload;
        const append = action.meta.arg?.append;
        const incoming = raw?.items ?? [];
        if (append) {
          const ids = new Set(state.items.map((i) => i._id));
          incoming.forEach((item) => {
            if (item?._id && !ids.has(item._id)) {
              state.items.push(item);
              ids.add(item._id);
            }
          });
        } else {
          state.items = incoming;
        }
        if (raw) {
          state.total = raw.total ?? state.total;
          if (typeof raw.unreadCount === 'number') {
            state.unreadCount = raw.unreadCount;
          }
          const limitVal =
            raw.limit ??
            action.meta.arg?.limit ??
            state.limit ??
            20;
          state.limit = limitVal;
          state.page = raw.page ?? action.meta.arg?.page ?? state.page;

          const totalVal =
            typeof raw.total === 'number' ? raw.total : state.total;
          const fromApi =
            typeof raw.totalPages === 'number' && raw.totalPages >= 0
              ? raw.totalPages
              : null;
          const derived =
            limitVal > 0 && typeof totalVal === 'number' && totalVal >= 0
              ? Math.ceil(totalVal / limitVal)
              : null;
          state.totalPages =
            fromApi != null
              ? fromApi
              : derived != null
                ? derived
                : state.totalPages;
        }
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.listLoading = false;
        state.listLoadingMore = false;
        state.listError = action.payload;
      })

      .addCase(fetchNotificationUnreadCount.pending, (state) => {
        state.unreadLoading = true;
      })
      .addCase(fetchNotificationUnreadCount.fulfilled, (state, action) => {
        state.unreadLoading = false;
        const raw = action.payload?.data ?? action.payload;
        if (raw && typeof raw.unreadCount === 'number') {
          state.unreadCount = raw.unreadCount;
        }
      })
      .addCase(fetchNotificationUnreadCount.rejected, (state) => {
        state.unreadLoading = false;
      })

      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const item = state.items.find((i) => i._id === notificationId);
        if (item && !item.isRead) {
          item.isRead = true;
          state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
        }
      })

      .addCase(markNotificationsReadForContext.fulfilled, (state, action) => {
        const ids = new Set(action.payload?.notificationIds ?? []);
        if (ids.size === 0) return;
        state.items.forEach((item) => {
          if (ids.has(item._id) && !item.isRead) {
            item.isRead = true;
          }
        });
        state.unreadCount = Math.max(
          0,
          (state.unreadCount || 0) - ids.size
        );
      })

      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.items.forEach((i) => {
          i.isRead = true;
        });
        state.unreadCount = 0;
      })

      .addCase(deleteNotification.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const item = state.items.find((i) => i._id === notificationId);
        state.items = state.items.filter((i) => i._id !== notificationId);
        state.total = Math.max(0, state.total - 1);
        if (item && !item.isRead) {
          state.unreadCount = Math.max(0, (state.unreadCount || 0) - 1);
        }
      });
  },
});

export const {
  resetNotificationsState,
  notificationReceivedFromSocket,
  notificationReadFromSocket,
  syncUnreadCount,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
